import { Context, useContext } from "react";
import { ZustandSelector } from "src/types/zustand";
import { StoreApi, useStore as useZustandStore } from "zustand";

/**
 * Extra no-op function call required for good TypeScript support.
 *
 * @example const useSomeContextStore = createUseContextStore<SomeStore>()(SomeContext, (context) => context.store);
 */
const createUseContextStore = <TStore extends object>() => {
	const createUseContextStoreInternal = <
		TApi extends StoreApi<TStore>,
		TContext extends object | null,
	>(
		context: Context<TContext>,
		storeSelector: (context: NonNullable<TContext>) => TApi,
	) => {
		const useContextStore = () => {
			const ctx = useContext(context);
			if (!ctx) {
				throw new Error(`Invalid context usage for store`);
			}
			return storeSelector(ctx);
		};
		const useStore = <U extends any>(selector: ZustandSelector<TStore, U>): U => {
			const store = useContextStore();
			return useZustandStore(store, selector);
		};
		return useStore;
	};
	return createUseContextStoreInternal;
};
export default createUseContextStore;
